import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Globe, User, Building2, Image, Save, Loader2, Eye,
  Mail, Phone, MessageCircle, Facebook, Instagram, Linkedin,
  Youtube, MapPin, Award, Star, Palette, Settings, Link as LinkIcon,
  CheckCircle, Camera, Sparkles
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SPECIALTIES = [
  'Residencial', 'Comercial', 'Industrial', 'Terrenos', 'Lujo',
  'Inversión', 'Preventas', 'Remates', 'Rentals', 'Corporativo'
];

const SERVICE_AREAS_MX = [
  'Ciudad de México', 'Guadalajara', 'Monterrey', 'Puebla', 'Querétaro',
  'Cancún', 'Mérida', 'León', 'Tijuana', 'San Miguel de Allende'
];

export default function MicrositePage() {
  const [microsite, setMicrosite] = useState(null);
  const [hasMicrosite, setHasMicrosite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Form state for creating
  const [slug, setSlug] = useState('');
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  
  // Edit form state
  const [formData, setFormData] = useState({
    display_name: '',
    business_name: '',
    tagline: '',
    bio: '',
    public_email: '',
    public_phone: '',
    public_whatsapp: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    youtube_url: '',
    city: '',
    state: '',
    service_areas: [],
    specialties: [],
    theme_color: '#4F46E5',
    show_alliances: true,
    show_ally_properties: true,
    show_testimonials: true,
    meta_title: '',
    meta_description: ''
  });

  const token = localStorage.getItem('upim_token');
  const navigate = useNavigate();

  useEffect(() => {
    loadMicrosite();
  }, []);

  const loadMicrosite = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/microsites/my-microsite`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setHasMicrosite(res.data.has_microsite);
      if (res.data.microsite) {
        setMicrosite(res.data.microsite);
        setFormData({
          display_name: res.data.microsite.display_name || '',
          business_name: res.data.microsite.business_name || '',
          tagline: res.data.microsite.tagline || '',
          bio: res.data.microsite.bio || '',
          public_email: res.data.microsite.public_email || '',
          public_phone: res.data.microsite.public_phone || '',
          public_whatsapp: res.data.microsite.public_whatsapp || '',
          facebook_url: res.data.microsite.facebook_url || '',
          instagram_url: res.data.microsite.instagram_url || '',
          linkedin_url: res.data.microsite.linkedin_url || '',
          youtube_url: res.data.microsite.youtube_url || '',
          city: res.data.microsite.city || '',
          state: res.data.microsite.state || '',
          service_areas: res.data.microsite.service_areas || [],
          specialties: res.data.microsite.specialties || [],
          theme_color: res.data.microsite.theme_color || '#4F46E5',
          show_alliances: res.data.microsite.show_alliances ?? true,
          show_ally_properties: res.data.microsite.show_ally_properties ?? true,
          show_testimonials: res.data.microsite.show_testimonials ?? true,
          meta_title: res.data.microsite.meta_title || '',
          meta_description: res.data.microsite.meta_description || ''
        });
      }
    } catch (err) {
      console.error('Error loading microsite:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkSlugAvailability = async (value) => {
    if (!value || value.length < 3) {
      setSlugAvailable(null);
      return;
    }
    
    setCheckingSlug(true);
    try {
      const res = await axios.get(`${API_URL}/api/microsites/check-slug/${value}`);
      setSlugAvailable(res.data.available);
    } catch (err) {
      setSlugAvailable(false);
    } finally {
      setCheckingSlug(false);
    }
  };

  const createMicrosite = async () => {
    if (!slug || !slugAvailable) {
      alert('Por favor elige un URL válido y disponible');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/microsites/create`,
        { slug },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadMicrosite();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear micrositio');
    } finally {
      setSaving(false);
    }
  };

  const updateMicrosite = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/microsites/update`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Micrositio actualizado');
      loadMicrosite();
    } catch (err) {
      alert('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </>
    );
  }

  // Show create form if no microsite
  if (!hasMicrosite) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                  <Globe className="w-10 h-10 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900">Crea tu Micrositio</h1>
                <p className="text-gray-500 mt-2">Tu página web profesional como asesor UPIM</p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Elige tu URL personalizada
                  </label>
                  <div className="flex items-center">
                    <span className="px-4 py-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-gray-500">
                      upim.org/asesor/
                    </span>
                    <input
                      type="text"
                      value={slug}
                      onChange={(e) => {
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setSlug(value);
                        checkSlugAvailability(value);
                      }}
                      placeholder="tu-nombre"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  {checkingSlug && (
                    <p className="text-sm text-gray-500 mt-2 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Verificando disponibilidad...
                    </p>
                  )}
                  {slugAvailable === true && (
                    <p className="text-sm text-green-600 mt-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> ¡URL disponible!
                    </p>
                  )}
                  {slugAvailable === false && (
                    <p className="text-sm text-red-600 mt-2">
                      Este URL ya está en uso, prueba con otro
                    </p>
                  )}
                </div>

                <div className="bg-indigo-50 rounded-lg p-4">
                  <h3 className="font-medium text-indigo-900 mb-2">Tu micrositio incluirá:</h3>
                  <ul className="text-sm text-indigo-700 space-y-1">
                    <li>✓ Perfil profesional con foto y datos de contacto</li>
                    <li>✓ Catálogo de tus propiedades</li>
                    <li>✓ Sección de aliados comerciales</li>
                    <li>✓ Propiedades de tus aliados UPIM</li>
                    <li>✓ Testimonios de clientes</li>
                    <li>✓ Formulario de contacto</li>
                    <li>✓ Credencial UPIM verificable</li>
                  </ul>
                </div>

                <button
                  onClick={createMicrosite}
                  disabled={saving || !slugAvailable}
                  className="w-full py-4 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Sparkles className="w-5 h-5" />
                  )}
                  Crear Mi Micrositio
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Edit existing microsite
  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Globe className="w-8 h-8 text-indigo-600" />
                Mi Micrositio
              </h1>
              <a 
                href={`${API_URL.replace('/api', '')}/api/microsites/public/${microsite?.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 hover:underline flex items-center gap-1 mt-1"
              >
                <LinkIcon className="w-4 h-4" />
                upim.org/asesor/{microsite?.slug}
              </a>
            </div>
            <div className="flex gap-3">
              <a
                href={`/asesor/${microsite?.slug}`}
                target="_blank"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Eye className="w-4 h-4" />
                Ver Sitio
              </a>
              <button
                onClick={updateMicrosite}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Guardar
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Visitas', value: microsite?.views_count || 0, icon: Eye },
              { label: 'Contactos', value: microsite?.contact_clicks || 0, icon: MessageCircle },
              { label: 'Verificado', value: microsite?.is_verified ? 'Sí' : 'Pendiente', icon: CheckCircle },
              { label: 'Estado', value: microsite?.is_active ? 'Activo' : 'Inactivo', icon: Globe },
            ].map((stat, i) => (
              <div key={i} className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto">
            {[
              { id: 'profile', name: 'Perfil', icon: User },
              { id: 'contact', name: 'Contacto', icon: Phone },
              { id: 'social', name: 'Redes', icon: Instagram },
              { id: 'settings', name: 'Configuración', icon: Settings },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </div>

          {/* Form Content */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre para mostrar</label>
                    <input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => handleChange('display_name', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Tu nombre profesional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de agencia (opcional)</label>
                    <input
                      type="text"
                      value={formData.business_name}
                      onChange={(e) => handleChange('business_name', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Nombre de tu inmobiliaria"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eslogan / Tagline</label>
                  <input
                    type="text"
                    value={formData.tagline}
                    onChange={(e) => handleChange('tagline', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Tu frase profesional"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Biografía</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => handleChange('bio', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Cuéntale a tus clientes sobre ti..."
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Ciudad de México"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="CDMX"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Especialidades</label>
                  <div className="flex flex-wrap gap-2">
                    {SPECIALTIES.map(spec => (
                      <button
                        key={spec}
                        type="button"
                        onClick={() => toggleArrayItem('specialties', spec)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          formData.specialties.includes(spec)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {formData.specialties.includes(spec) && <CheckCircle className="w-3 h-3 inline mr-1" />}
                        {spec}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'contact' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email público
                  </label>
                  <input
                    type="email"
                    value={formData.public_email}
                    onChange={(e) => handleChange('public_email', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.public_phone}
                    onChange={(e) => handleChange('public_phone', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="+52 55 1234 5678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <MessageCircle className="w-4 h-4 inline mr-1" />
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={formData.public_whatsapp}
                    onChange={(e) => handleChange('public_whatsapp', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="+52 55 1234 5678"
                  />
                </div>
              </div>
            )}

            {activeTab === 'social' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Facebook className="w-4 h-4 inline mr-1" />
                    Facebook
                  </label>
                  <input
                    type="url"
                    value={formData.facebook_url}
                    onChange={(e) => handleChange('facebook_url', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://facebook.com/tu-pagina"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Instagram className="w-4 h-4 inline mr-1" />
                    Instagram
                  </label>
                  <input
                    type="url"
                    value={formData.instagram_url}
                    onChange={(e) => handleChange('instagram_url', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://instagram.com/tu-cuenta"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Linkedin className="w-4 h-4 inline mr-1" />
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => handleChange('linkedin_url', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://linkedin.com/in/tu-perfil"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Youtube className="w-4 h-4 inline mr-1" />
                    YouTube
                  </label>
                  <input
                    type="url"
                    value={formData.youtube_url}
                    onChange={(e) => handleChange('youtube_url', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="https://youtube.com/@tu-canal"
                  />
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Palette className="w-4 h-4 inline mr-1" />
                    Color del tema
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={formData.theme_color}
                      onChange={(e) => handleChange('theme_color', e.target.value)}
                      className="w-12 h-12 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.theme_color}
                      onChange={(e) => handleChange('theme_color', e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg w-32"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Visibilidad de secciones</label>
                  
                  {[
                    { key: 'show_alliances', label: 'Mostrar aliados comerciales' },
                    { key: 'show_ally_properties', label: 'Mostrar propiedades de aliados' },
                    { key: 'show_testimonials', label: 'Mostrar testimonios' },
                  ].map(opt => (
                    <label key={opt.key} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData[opt.key]}
                        onChange={(e) => handleChange(opt.key, e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-gray-700">{opt.label}</span>
                    </label>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta título (SEO)</label>
                  <input
                    type="text"
                    value={formData.meta_title}
                    onChange={(e) => handleChange('meta_title', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Título para buscadores"
                    maxLength={60}
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.meta_title.length}/60 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta descripción (SEO)</label>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => handleChange('meta_description', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Descripción para buscadores"
                    maxLength={160}
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.meta_description.length}/160 caracteres</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
